import './Filter.scss';
import { z } from 'zod';

import FilterTools from './FilterTools';
import FilterConfig from './FilterConfig';
import type { API_ObjectAttributesType } from '../../types-telematics';
import type { FilterSubMenuValue, RelationsType, RelationType } from '../../types-analytics';
import { FilterColumnsSchema, FilterGroupsSchema } from '../../types';
import type {
    FilterColumns,
    FilterGroups,
    FilterHumanReadableType,
    QueriesType,
    QueryGeometryType,
    QueryType,
} from '../../types';
import type { FilterEditorItemType } from './FilterEditorItem';

import FilterLocale from './FilterLocale';
import type { FilterManager } from './FilterManager';

export const AvailableValueSchema = z.object({
    // id: z.union([z.string(), z.number()]),
    id: z.coerce.string(),
    name: z.string(),
});
export type AvailableValueType = z.infer<typeof AvailableValueSchema>;

export const AvailableValuesSchema = z.record(z.array(AvailableValueSchema));
export type AvailableValuesType = z.infer<typeof AvailableValuesSchema>;

class Filter extends FilterTools {
    private filterQueries: QueriesType = {};
    private filterRelated: RelationsType = [];

    private availableGroupEntries: { id: number | string; name: string }[] = [];
    private availableTypes: API_ObjectAttributesType[] = [];
    // public availableValues: { id: string | number, name: string }[] = [];
    public availableValues: AvailableValuesType = {};
    private filterItemCategories: string[] = [];
    private filterColumns: FilterColumns | undefined;
    private filterGroups: FilterGroups | undefined;
    private subMenuValues: { [key: string]: FilterSubMenuValue } = {};

    public andOrSelection: string = 'and'; // $and
    public andOrSelectionEnabled: boolean = true;

    constructor() {
        super();
        this.filterQueries = {};
        this.filterRelated = [];
    }

    isEmpty(): boolean {
        return !Object.keys(this.filterQueries).length && !this.filterRelated.length;
    }

    getTypeKeysColorableSlider(): string[] {
        let standard_keys: string[] = this.availableTypes
            .filter((item: API_ObjectAttributesType) => {
                return (
                    item.colorable &&
                    item.reference === null &&
                    item.max !== null &&
                    item.min !== null &&
                    !item.can_be_related
                );
            })
            .map((item: API_ObjectAttributesType) => item.name);

        let related_keys: string[] = this.availableTypes
            .filter((item: API_ObjectAttributesType) => {
                return item.colorable && item.can_be_related;
            })
            .map((item: API_ObjectAttributesType) => item.name);

        return [...standard_keys, ...related_keys];
    }

    getTypeKeysColorableGroup(): string[] {
        return this.availableTypes
            .filter((item: API_ObjectAttributesType) => {
                return item.colorable && item.reference !== null; //&& this.getAvailableValues(item.reference).length > 0;
            })
            .map((item: API_ObjectAttributesType) => item.name);
    }

    getTypeKeysFirstPossible(): string {
        // get first element from union of two arrays from getTypeKeysColorableSlider() and getTypeKeysColorableGroup()
        return [...this.getTypeKeysColorableSlider(), ...this.getTypeKeysColorableGroup()][0];
    }

    setFilterColumnsAndGroups(filterColumns: FilterColumns, filterGroups: FilterGroups): void {
        // if (typeof filterColumns === 'undefined') {
        //     throw new Error('FilterColumns are not set');
        // }
        // if (typeof filterGroups === 'undefined') {
        //     throw new Error('FilterGroups are not set');
        // }

        try {
            filterColumns = FilterColumnsSchema.parse(filterColumns);
        } catch (e) {
            console.error('ZodError, FilterColumnsSchema.parse', e.message);
        }

        try {
            filterGroups = FilterGroupsSchema.parse(filterGroups);
        } catch (e) {
            console.error('ZodError, FilterGroupsSchema.parse', e.message);
        }
        // console.log('Filter, setFilterColumnsAndGroups, filterColumns, filterGroups', filterColumns, filterGroups);
        this.filterColumns = filterColumns;
        this.filterGroups = filterGroups;
    }

    updateSubMenuItems(key: string, value: FilterSubMenuValue): void {
        this.subMenuValues[key] = value;
    }

    getSubMenuItems(key: string): FilterSubMenuValue {
        return this.subMenuValues[key] ?? { label: '', items: [] };
    }

    getSubMenu(): { [key: string]: FilterSubMenuValue } {
        return this.subMenuValues;
    }

    isKeyUsedInSubMenu(key: string): boolean {
        if (typeof this.subMenuValues[key] !== 'undefined') return true;

        for (let k in this.subMenuValues) {
            if (this.subMenuValues[k].items.map(i => i.id).includes(key)) return true;
        }

        return false;
    }

    getFilterColumns(): FilterColumns {
        if (typeof this.filterColumns === 'undefined') {
            throw new Error('FilterColumns are not set. Use setFilterColumnsAndGroups()');
        }
        return this.filterColumns;
    }

    getFilterGroups(): FilterGroups {
        if (typeof this.filterGroups === 'undefined') {
            throw new Error('FilterGroups are not set. Use setFilterColumnsAndGroups()');
        }
        return this.filterGroups;
    }

    getNameForItemKey(key: string): string {
        let subtype: string | null = null;
        if (key.indexOf('/') >= 0) {
            let keyarr = key.split('/');
            key = keyarr[0];
            subtype = keyarr[1];
        }
        return (
            this.availableTypes
                .filter((item: API_ObjectAttributesType) => {
                    return item.name === key;
                })
                .map((item: API_ObjectAttributesType) => {
                    return subtype ? item.subtype[subtype].label : item.desc;
                })[0] ?? key
        );
    }

    getFilterableForItemKey(key: string): boolean {
        return (
            this.availableTypes
                .filter((item: API_ObjectAttributesType) => {
                    return item.name === key;
                })
                .map((item: API_ObjectAttributesType) => item.filterable)[0] ?? false
        );
    }

    getAvailableTypeByName(name: string): API_ObjectAttributesType | undefined {
        return this.availableTypes.filter((item: API_ObjectAttributesType) => {
            return item.name === name;
        })[0];
    }

    // Return more menu items, if possible (for ex. for `geom` it should return `geom/area` and `geom/radius`)
    getMoreKeysForThisKey(key: string): string[] {
        if (key in FilterConfig.filtersForAttribute) {
            return FilterConfig.filtersForAttribute[key].map((k: string) => {
                return key + '/' + k;
            });
        } else {
            return [key];
        }
    }

    getCountFilterRules(): number {
        return Object.keys(this.filterQueries).length + this.filterRelated.length;
    }

    isKeyUsed(key: string): boolean {
        return (
            this.filterQueries[key] !== undefined || this.getFilterRelatedItem(key) !== undefined
        );
    }

    getFieldData(variableName: string): { name: string; displayMethod: string } {
        return {
            name: variableName,
            displayMethod: this.getDisplayMethod(variableName),
        };
    }

    getDisplayMethod(variableName: string): string {
        if (this.availableTypes.length === 0) {
            console.warn('getDisplayMethod, this.availableTypes is empty. Use setAvailableTypes()');
            return '';
        } else {
            let displayMethod: string = '';
            // console.log('displayMethod, variableName', variableName);
            this.availableTypes.forEach(attrs => {
                if (attrs.name === variableName) {
                    // console.log('getDisplayMethod, attrs', attrs);
                    if (attrs.reference !== null) {
                        displayMethod = FilterConfig.displayMethod.SELECT_MULTIPLE;
                    } else {
                        displayMethod = this.getDisplayMethodByLongKey(attrs.data_type);
                    }
                    if (attrs.can_be_related) {
                        displayMethod = FilterConfig.displayMethod.RELATED;
                    }
                }
            });
            if (['geom/area', 'geom/radius'].includes(variableName)) {
                // Try to get 'geom/area' or 'geom/radius' from 'geometry' --- #FIXME:
                displayMethod = this.getDisplayMethodByLongKey(variableName);
            }
            // console.log('getDisplayMethod, displayMethod', displayMethod);
            if (displayMethod !== '') {
                // console.log('getDisplayMethod', variableName, 'displayMethod', displayMethod);
                return displayMethod;
            }
            console.error(
                'getDisplayMethod this.availableTypes, Not found displayMethod for variableName: ',
                variableName,
                this.availableTypes
            );
            // throw new Error('getDisplayMethod, Variable not found: ' + variableName);
        }
    }

    getDisplayMethodByLongKey(longType: string): string {
        switch (longType) {
            case 'date':
            case 'time':
                return FilterConfig.displayMethod.BETWEEN;
            case 'datetime':
                return FilterConfig.displayMethod.BETWEEN;
            case 'int':
                return FilterConfig.displayMethod.BETWEEN;
            case 'double':
                return FilterConfig.displayMethod.BETWEEN;
            case 'string':
                return FilterConfig.displayMethod.SINGLE;
            case 'bool':
                return FilterConfig.displayMethod.SELECT_SINGLE;
            case 'geometry':
                return FilterConfig.displayMethod.AREA;
            case 'geom/area':
                return FilterConfig.displayMethod.AREA;
            case 'geom/radius':
                return FilterConfig.displayMethod.RADIUS;
            case 'int_array':
                return FilterConfig.displayMethod.SELECT_MULTIPLE;
            case 'string_array':
                return FilterConfig.displayMethod.SELECT_MULTIPLE;
            case 'money':
                return FilterConfig.displayMethod.RELATED;
            default:
                console.error(
                    'Not recognized type of object (cannot select display method): ',
                    longType,
                    this.availableTypes
                );
                throw new Error(
                    'Not recognized type of object (cannot select display method): ' + longType
                );
            // return FilterConfig.displayMethod.SINGLE;
        }
    }

    getAvailableValuesAll() {
        return this.availableValues;
    }

    setAvailableValuesAll(availableValues) {
        this.availableValues = availableValues;
    }

    getAvailableValues(key: string): { id: string; name: string }[] {
        // console.log('Filter, getAvailableValues, key, this.availableValues', key, this.availableValues);
        return this.availableValues[key];
    }

    setAvailableValues(key: string, values: { id: string | number; name: string }[]) {
        this.availableValues[key] = values;
    }

    getSelectedValues(key: string): string | number | (string | number)[] | null {
        // console.log('Filter, getSelectedValues, key, this.filterQueries[key]', key, this.filterQueries[key]);
        try {
            // console.log('getSelectedValues', this.extractFromMongo(this.filterQueries[key]).values);
            return this.extractFromMongo(key, this.filterQueries[key]).values;
        } catch (err) {
            return [];
        }
    }

    updateSelectedValues(key: string, newValues: string[] | number[] | string | number) {
        // console.log('Filter, updateSelectedValues, key, newValues, (old)this.filterQueries', newValues, key, this.filterQueries);
        let filterItem = {};
        filterItem[key] = this.filterQueries[key];
        let { variable, condition, values } = this.extractFromMongo(key, this.filterQueries[key]);
        if (variable !== key) {
            console.warn('Variable name in filter does not match key.', variable, key);
            console.warn(
                '{ variable, condition, values }',
                { variable, condition, values },
                filterItem
            );
            throw new Error('Variable name in filter does not match key.');
        }
        let typeToConvert = this.availableTypes.filter(type => type.name === key)[0].data_type;
        this.updateFilterItem(
            key,
            this.convertToFilterItem(
                {
                    variable: key,
                    condition: condition,
                    values: this._getParsedValueIfNeeded(newValues, typeToConvert),
                },
                typeToConvert
            )
        );
    }

    setAvailableTypes(availableTypes: API_ObjectAttributesType[]): void {
        this.availableTypes = availableTypes;
    }

    getAvailableTypes(): API_ObjectAttributesType[] {
        return this.availableTypes;
    }

    setFilterQueries(queries: QueriesType): void {
        if (Array.isArray(queries)) {
            console.warn('Filter rules must be an object, not an array.', queries);
            this.filterQueries = {};
        } else {
            // console.log('Filter, setFilter, rules', queries);
            this.filterQueries = queries;
        }
    }

    getFilterQueries(): QueriesType {
        return this.filterQueries;
    }

    updateFilterItem(key: string, mongoCondition: QueryType | QueryGeometryType): string {
        if (typeof mongoCondition['$geoWithin'] === 'undefined') {
            this._updateFilterItemStandard(key, mongoCondition as QueryType);
        } else {
            this._updateFilterItemGeometry(key, mongoCondition as QueryGeometryType);
        }
        return key;
    }

    _updateFilterItemStandard(key: string, mongoCondition: QueryType): string {
        if (typeof mongoCondition['$geoWithin'] !== 'undefined') {
            throw new Error('Use updateFilterItemGeometry for $geoWithin');
        }
        if (typeof mongoCondition['$geometry'] !== 'undefined') {
            throw new Error(
                '$geometry cannot be used directly. Use $geoWithin and use it with updateFilterItemGeometry()'
            );
        }
        this.filterQueries[key] = mongoCondition;
        return key;
    }

    _updateFilterItemGeometry(key: string, mongoCondition: QueryGeometryType): string {
        if (typeof mongoCondition['$geoWithin'] !== 'undefined') {
            if (typeof mongoCondition['$geoWithin']['$geometry'] === 'undefined') {
                console.trace(
                    'Filter, updateFilterItem, Defined [$geoWithin] but not defined [$geoWithin][$geometry], key, mongoCondition ',
                    key,
                    mongoCondition
                );
                throw new Error(
                    'Filter, updateFilterItem, [$geoWithin][$geometry] has invalid definition. See console above.'
                );
            }
        }
        this.filterQueries[key] = mongoCondition;
        return key;
    }

    getFilterItem(key: string): QueryType {
        return this.filterQueries[key] ?? {};
    }

    removeFilterItem(key: string): void {
        if (typeof this.filterQueries[key] !== 'undefined') {
            // if (this.)
            delete this.filterQueries[key];
        } else {
            this.removeFilterRelatedItem(key);
        }
    }

    setAvailableGroupsEntries(availableGroupEntries) {
        this.availableGroupEntries = availableGroupEntries;
    }

    getAvailableGroupsEntries() {
        return this.availableGroupEntries;
    }

    getFilterDescription() {
        return JSON.stringify(this.filterQueries);
    }

    getFilterHumanReadableAsArray(filterManager: FilterManager): FilterHumanReadableType[] {
        // console.log('getFilterHumanReadableAsArray, this.filterQueries', this.filterQueries);
        let filterHumanReadable: FilterHumanReadableType[] = [];
        // Get filterItem from this.filterQueries
        Object.keys(this.filterQueries).forEach(ruleKey => {
            let filterItem: QueryType = this.filterQueries[ruleKey];
            let ruleKeyLong = ruleKey;
            let filter_str = '';
            let parameters = [];
            let availableType: API_ObjectAttributesType = this.getAvailableTypeByName(ruleKey);
            if (typeof availableType === 'undefined') {
                console.error(
                    'getFilterHumanReadableAsArray, not found ruleKey in this.availableTypes.name[] ',
                    ruleKey,
                    this.availableTypes.map(type => type.name)
                );
            } else {
                if (ruleKey == 'geom') {
                    try {
                        if (filterItem?.properties?.filter == 'radius') {
                            ruleKeyLong = 'geom/radius';
                            parameters.push(
                                filterItem.properties.radius,
                                filterItem.properties.label
                            );
                        } else {
                            ruleKeyLong = 'geom/area';
                        }
                    } catch (err) {
                        console.warn(err?.message);
                    }
                    if (filterItem?.properties?.filter) {
                        ruleKeyLong = 'geom/' + filterItem.properties.filter;
                    }

                    if (
                        availableType.subtype &&
                        availableType?.subtype[ruleKeyLong.split('/')[1]]
                    ) {
                        filter_str = availableType?.subtype[ruleKeyLong.split('/')[1]].filter_str;
                    }
                } else {
                    filter_str = availableType?.filter_str;
                }
                // let ruleKey = filterItem[0];
                // console.log('getFilterHumanReadableAsArray, filterItem', filterItem, this.filterQueries, this.getAvailableTypes());
                let unit: string = '';
                if (this.getAvailableTypes().filter(type => type.name === ruleKey)[0]?.unit) {
                    unit =
                        this.getAvailableTypes().filter(type => type.name === ruleKey)[0].unit ??
                        '';
                }
                filterHumanReadable.push({
                    active: filterManager.featureInactiveQueries.getEnabled()
                        ? filterManager.featureInactiveQueries.isActiveQuery(ruleKey)
                        : null,
                    key: ruleKey,
                    description: this.translateFilterItem(
                        ruleKey,
                        filterItem,
                        unit,
                        filter_str,
                        parameters,
                        availableType.data_type
                    ),
                    displayMethod: this.getDisplayMethod(ruleKeyLong),
                    allAvailableEntries: this.getAvailableValues(ruleKey),
                    selectedIds: (typeof this.getSelectedValues(ruleKey) === 'object' &&
                    this.getSelectedValues(ruleKey) !== null &&
                    this.getSelectedValues(ruleKey).hasOwnProperty('length')
                        ? this.getSelectedValues(ruleKey)
                        : []) as string[] | number[],
                });
            }
        });

        this.getFilterRelated().forEach(relation => {
            filterHumanReadable.push({
                key: relation.id,
                description: new FilterLocale().getDescriptionForRelation(
                    relation,
                    this.getAvailableType(relation.id)
                ),
                displayMethod: FilterConfig.displayMethod.RELATED,
                allAvailableEntries: [],
                selectedIds: [],
                active: true,
            });
        });

        // console.log('getFilterHumanReadableAsArray, filterHumanReadable', filterHumanReadable);
        return filterHumanReadable;
    }

    getAvailableType(key: string): API_ObjectAttributesType {
        return this.getAvailableTypes().filter(type => type.name === key)[0];
    }

    getRequestQuery() {
        if (Object.keys(this.filterQueries).length) {
            return { filter: this.filterQueries };
        } else {
            return {};
        }
    }

    // getColorableTypesKeys() {
    //     return this.getAvailableTypes().filter(type => type.colorable === true).map(type => type.name);
    // }

    getOrderedFilterItemCategories() {
        // return Object.values(this.filterItemCategories).sort((a, b) => a.order - b.order);
        return Object.keys(this.filterItemCategories);
    }

    setFilterRelated(filterRelated: RelationType[]): void {
        this.filterRelated = filterRelated;
    }

    getFilterRelated(): RelationType[] {
        return this.filterRelated;
    }

    getFilterRelatedKeys(): string[] {
        return this.filterRelated.map(related => related.id);
    }

    updateFilterRelatedItem(key: string, fields: RelationType): void {
        this.filterRelated = this.filterRelated.filter(related => related.id !== key);
        this.filterRelated.push(fields);
        console.log(
            'getFilterRelated, updateFilterRelatedItem',
            this.getFilterRelated(),
            key,
            fields
        );
    }

    removeFilterRelatedItem(key: string): void {
        this.filterRelated = this.filterRelated.filter(related => related.id !== key);
    }

    getFilterRelatedItem(key: string): RelationType {
        // console.log('filter', this.filterQueries);
        return this.filterRelated.filter(related => related.id === key)[0];
    }

    getFilterRelatedItemDefault(
        key: string,
        filterItem: FilterEditorItemType,
        type: string
    ): RelationType {
        let d = new Date();
        let base_period = d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).substr(-2);
        if (base_period > filterItem.period_range.max) {
            base_period = filterItem.period_range.max;
        }
        let cmp_period_arr = base_period.split('-').map(e => parseInt(e));
        if (cmp_period_arr[1] > 1) {
            cmp_period_arr[1]--;
        } else {
            cmp_period_arr[0]--;
            cmp_period_arr[1] = 12;
        }
        let cmp_period = cmp_period_arr[0] + '-' + ('0' + cmp_period_arr[1]).substr(-2);
        return {
            id: key,
            type: type,
            c: '><',
            unit: type === 'period' ? '%' : filterItem.unit,
            v: [],
            db_name: filterItem.collection,
            base_period: base_period,
            second_period: cmp_period,
            comparing_field: filterItem.fld_period,
            a: 'max',
        };
    }
}

export default Filter;
